// `import type`, deliberately: leaveReportExport pulls in jspdf at module load, and jspdf probes
// for a canvas as a side effect. A type-only import is erased, so this module stays free of that —
// it needs the shared request shape, not the PDF writer.
import type { ExportColumn, ExportRequest } from './leaveReportExport';

/**
 * Real `.xlsx` output, with no new dependency.
 *
 * The other two formats live in leaveReportExport.ts and are driven from the same
 * `columns` + `rows` pair; this is the third, wired to that same pair for the same reason —
 * three generators that each decide what a report contains would eventually disagree.
 *
 * Why hand-rolled rather than SheetJS/ExcelJS: the existing exporter documents the deliberate
 * choice to ship `.csv` because it "needs no new dependency", and a reporting page is a thin
 * enough reason to add a multi-megabyte spreadsheet library to the bundle. An `.xlsx` is a ZIP of
 * five small XML parts, and a ZIP written with the *store* (uncompressed) method needs no deflate
 * implementation — only a CRC-32. That is the whole of what follows.
 *
 * `.csv` is kept alongside this rather than replaced: it stays the format that opens anywhere,
 * and this is the one that keeps the header row frozen, the columns filterable and numbers stored
 * as numbers instead of as text Excel has to guess at.
 */

// ── CRC-32 ────────────────────────────────────────────────────────────────────

/** Standard IEEE 802.3 polynomial table, built once on first use. */
let crcTable: Uint32Array | null = null;

const getCrcTable = (): Uint32Array => {
  if (crcTable) return crcTable;
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  crcTable = table;
  return table;
};

const crc32 = (bytes: Uint8Array): number => {
  const table = getCrcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
};

// ── ZIP (store method) ────────────────────────────────────────────────────────

interface ZipEntry {
  name: string;
  bytes: Uint8Array;
}

/**
 * DOS date/time, as the ZIP local header wants it.
 *
 * Below 1980 is unrepresentable in the format, so anything earlier is clamped rather than
 * silently wrapping into a nonsense year.
 */
const dosDateTime = (d: Date): { time: number; date: number } => {
  const year = Math.max(d.getFullYear(), 1980);
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (Math.floor(d.getSeconds() / 2) & 0x1f),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
};

/**
 * Pack entries into a ZIP archive with no compression.
 *
 * Store rather than deflate keeps this dependency-free. A report's XML is highly compressible, so
 * the file is larger than a deflated one would be — but these are tens of kilobytes of text, and
 * an uncompressed .xlsx is as valid as a compressed one to every reader that opens it.
 */
const buildZip = (entries: ZipEntry[], now: Date): Blob => {
  const { time, date } = dosDateTime(now);
  const encoder = new TextEncoder();

  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name);
    const crc = crc32(entry.bytes);
    const size = entry.bytes.length;

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true); // local file header signature
    lv.setUint16(4, 20, true);         // version needed to extract
    lv.setUint16(6, 0x0800, true);     // flags: UTF-8 file names
    lv.setUint16(8, 0, true);          // method: store
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);      // compressed size == uncompressed under store
    lv.setUint32(22, size, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);         // extra field length
    local.set(nameBytes, 30);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true); // central directory header signature
    cv.setUint16(4, 20, true);         // version made by
    cv.setUint16(6, 20, true);         // version needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);         // extra
    cv.setUint16(32, 0, true);         // comment
    cv.setUint16(34, 0, true);         // disk number start
    cv.setUint16(36, 0, true);         // internal attributes
    cv.setUint32(38, 0, true);         // external attributes
    cv.setUint32(42, offset, true);    // offset of local header
    central.set(nameBytes, 46);

    locals.push(local, entry.bytes);
    centrals.push(central);
    offset += local.length + size;
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);

  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);   // end of central directory signature
  ev.setUint16(4, 0, true);            // this disk
  ev.setUint16(6, 0, true);            // disk with central directory
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  ev.setUint16(20, 0, true);           // comment length

  return new Blob([...locals, ...centrals, eocd] as BlobPart[], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

// ── SpreadsheetML ─────────────────────────────────────────────────────────────

/**
 * Escape text for XML content.
 *
 * The control-character strip is not decoration: Excel refuses to open a workbook containing a
 * raw control character, and free-text fields on these reports (a change reason typed into a
 * textarea) can carry them. Tab, newline and carriage return are legal and kept.
 */
const xmlText = (value: string): string =>
  value
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** 0 -> A, 25 -> Z, 26 -> AA. Spreadsheet column letters are base-26 with no zero digit. */
export const columnLetter = (index: number): string => {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
};

type CellValue = string | number;

/**
 * One cell.
 *
 * Numbers are written bare (`<v>`), which is what makes them numbers in the sheet — sortable,
 * summable, right-aligned — rather than text that merely looks numeric. Strings use `inlineStr`
 * so there is no shared-string table to build and keep consistent.
 *
 * A non-finite number (NaN, Infinity) would be written as an invalid `<v>` that Excel rejects,
 * so it degrades to text.
 */
const cellXml = (ref: string, value: CellValue, styleId: number): string => {
  const s = styleId ? ` s="${styleId}"` : '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"${s}><v>${value}</v></c>`;
  }
  const text = value === null || value === undefined ? '' : String(value);
  if (text === '') return `<c r="${ref}"${s}/>`;
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${xmlText(text)}</t></is></c>`;
};

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

/**
 * Style 1 is the header (bold white on the app's teal); style 2 is the report title; style 3 is a
 * muted label for the filter block. Fill slots 0 and 1 must be `none` and `gray125` — the format
 * reserves them, and shifting them corrupts every later fill index.
 */
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="4">
<font><sz val="11"/><name val="Calibri"/></font>
<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
<font><b/><sz val="14"/><color rgb="FF0F172A"/><name val="Calibri"/></font>
<font><sz val="10"/><color rgb="FF64748B"/><name val="Calibri"/></font>
</fonts>
<fills count="3">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF0D9488"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="1"><border/></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="4">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const STYLE_NORMAL = 0;
const STYLE_HEADER = 1;
const STYLE_TITLE = 2;
const STYLE_META = 3;

const workbookXml = (sheetName: string): string =>
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${xmlText(sheetName)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

/**
 * Excel caps a sheet name at 31 characters and forbids `: \ / ? * [ ]`. A report title like
 * "Department-wise Shift Report" is already close to the limit, so this trims rather than
 * letting Excel declare the file corrupt.
 */
const safeSheetName = (title: string): string => {
  const cleaned = title.replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim();
  return (cleaned || 'Report').slice(0, 31);
};

/**
 * Column widths, approximated from the widest value actually present.
 *
 * Without this every column arrives at Excel's default width and long fields — emails, employee
 * names, change reasons — show as `####` or get clipped, which is the first thing anyone notices
 * about a generated workbook. Capped so one long free-text reason cannot push a column off screen.
 */
const columnWidths = <T,>(columns: ExportColumn<T>[], rows: T[]): number[] =>
  columns.map(col => {
    let widest = col.header.length;
    // Sampled rather than exhaustive: 200 rows is enough to size a column, and a 20k-row
    // export should not pay a full extra pass over every cell to find that out.
    const sample = Math.min(rows.length, 200);
    for (let i = 0; i < sample; i++) {
      const len = String(col.value(rows[i]) ?? '').length;
      if (len > widest) widest = len;
    }
    return Math.min(Math.max(widest + 2, 9), 44);
  });

const sheetXml = <T,>(request: ExportRequest<T>, generatedAt: string): string => {
  const { columns, rows } = request;
  const meta = request.meta || [];

  const lines: string[] = [];
  let r = 0;

  // Title, then generation stamp, then one row per filter, then a spacer — the same preamble
  // the CSV and PDF carry, so a reader comparing two formats of one report sees one report.
  r++;
  lines.push(`<row r="${r}"><c r="A${r}" s="${STYLE_TITLE}" t="inlineStr"><is><t>${xmlText(request.title)}</t></is></c></row>`);
  r++;
  lines.push(`<row r="${r}"><c r="A${r}" s="${STYLE_META}" t="inlineStr"><is><t>Generated ${xmlText(generatedAt)}</t></is></c></row>`);
  for (const m of meta) {
    r++;
    lines.push(
      `<row r="${r}">` +
        `<c r="A${r}" s="${STYLE_META}" t="inlineStr"><is><t>${xmlText(m.label)}</t></is></c>` +
        `<c r="B${r}" s="${STYLE_META}" t="inlineStr"><is><t>${xmlText(m.value)}</t></is></c>` +
      `</row>`
    );
  }
  r++; // spacer row, left empty

  r++;
  const headerRow = r;
  lines.push(
    `<row r="${headerRow}" ht="20" customHeight="1">` +
      columns.map((c, i) => cellXml(`${columnLetter(i)}${headerRow}`, c.header, STYLE_HEADER)).join('') +
    `</row>`
  );

  for (const row of rows) {
    r++;
    // Bound per iteration rather than closing over the mutable `r`: the cell callbacks below must
    // see this row's number, and a shared mutable counter is how an off-by-one gets in.
    const rowIndex = r;
    lines.push(
      `<row r="${rowIndex}">` +
        columns.map((c, i) => cellXml(`${columnLetter(i)}${rowIndex}`, c.value(row), STYLE_NORMAL)).join('') +
      `</row>`
    );
  }

  const lastCol = columnLetter(Math.max(columns.length - 1, 0));
  const widths = columnWidths(columns, rows);
  const colsXml = widths
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join('');

  // Element order is fixed by the schema: dimension, sheetViews, sheetFormatPr, cols, sheetData,
  // then autoFilter. Out of order, Excel reports the file as unreadable.
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="A1:${lastCol}${Math.max(r, headerRow)}"/>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
<cols>${colsXml}</cols>
<sheetData>${lines.join('')}</sheetData>
<autoFilter ref="A${headerRow}:${lastCol}${Math.max(r, headerRow)}"/>
</worksheet>`;
};

// ── Public API ────────────────────────────────────────────────────────────────

const stamp = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
};

const fileBase = (title: string, d: Date): string =>
  `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${stamp(d)}`;

const triggerDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked on a later tick: revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * The workbook's XML parts, before they are zipped.
 *
 * Exported as the seam the tests read: asserting on the sheet XML checks the thing that can
 * actually be wrong (a number written as text, an unescaped `&`, a header row the freeze pane
 * misses), whereas asserting on the finished Blob only proves that some bytes were produced —
 * and jsdom's Blob has no `arrayBuffer()` to read them back with anyway.
 */
export function buildXlsxParts<T>(
  request: ExportRequest<T>,
  generatedAt: string
): Record<string, string> {
  return {
    '[Content_Types].xml': CONTENT_TYPES,
    '_rels/.rels': ROOT_RELS,
    'xl/workbook.xml': workbookXml(safeSheetName(request.title)),
    'xl/_rels/workbook.xml.rels': WORKBOOK_RELS,
    'xl/styles.xml': STYLES,
    'xl/worksheets/sheet1.xml': sheetXml(request, generatedAt),
  };
}

/** The workbook as a Blob, without touching the DOM. */
export function buildXlsxBlob<T>(request: ExportRequest<T>, now: Date = new Date()): Blob {
  const encoder = new TextEncoder();
  const parts = buildXlsxParts(request, now.toLocaleString());
  // Part order matters to some readers: the content-type map must be the first entry.
  const order = [
    '[Content_Types].xml',
    '_rels/.rels',
    'xl/workbook.xml',
    'xl/_rels/workbook.xml.rels',
    'xl/styles.xml',
    'xl/worksheets/sheet1.xml',
  ];
  const entries: ZipEntry[] = order.map(name => ({ name, bytes: encoder.encode(parts[name]) }));
  return buildZip(entries, now);
}

/** Excel workbook, from the same `columns` + `rows` pair as the CSV and the PDF. */
export function exportReportXlsx<T>(request: ExportRequest<T>): void {
  const now = new Date();
  triggerDownload(buildXlsxBlob(request, now), `${fileBase(request.title, now)}.xlsx`);
}
